import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnalyticsConsentService, AnalyticsConsentState } from '../services/AnalyticsConsentService';
import { GoogleAnalyticsService } from '../services/GoogleAnalyticsService';
import { PostHogService } from '../services/PostHogService';

const handleAnalyticsAccepted = () => {
  AnalyticsConsentService.setState('granted');
  GoogleAnalyticsService.enableAnalytics({ sendInitialPageView: true });
  PostHogService.init();
  PostHogService.trackPageView(undefined, typeof document !== 'undefined' ? document.title : undefined, {
    force: true,
    source: 'consent_banner'
  });
  PostHogService.capture('ops_analytics_consent_granted', {
    source: 'consent_banner',
    current_path: typeof window !== 'undefined' ? window.location.pathname : undefined
  });
};

const copy = {
  it: {
    title: 'Cookie e analytics',
    description:
      'Usiamo analytics di prodotto e session replay con input mascherati per capire cosa funziona meglio e migliorare TradingSpace. Puoi accettare o rifiutare i cookie non essenziali.',
    accept: 'Accetta analytics',
    reject: 'Rifiuta',
    learnMore: 'Leggi la parte legale'
  },
  en: {
    title: 'Cookies and analytics',
    description:
      'We use product analytics and masked session replay to understand what works and improve TradingSpace. You can accept or reject non-essential analytics cookies.',
    accept: 'Accept analytics',
    reject: 'Reject',
    learnMore: 'Read the legal details'
  }
} as const;

export const AnalyticsConsentBanner: React.FC = () => {
  const { i18n } = useTranslation();
  const [consentState, setConsentState] = useState<AnalyticsConsentState>(AnalyticsConsentService.getState());

  const activeLocale: 'it' | 'en' = useMemo(
    () => (i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'it'),
    [i18n.language]
  );

  useEffect(() => {
    return AnalyticsConsentService.subscribe(setConsentState);
  }, []);

  const hasAnalyticsProvider = PostHogService.isConfigured() || GoogleAnalyticsService.isConfigured();

  if (!hasAnalyticsProvider || consentState !== 'unknown') {
    return null;
  }

  const text = copy[activeLocale];

  return (
    <div
      style={{
        position: 'fixed',
        left: '20px',
        right: '20px',
        bottom: '20px',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'center'
      }}
    >
      <div
        style={{
          width: 'min(920px, 100%)',
          borderRadius: '24px',
          background: 'rgba(255, 255, 255, 0.96)',
          border: '1px solid rgba(17, 24, 39, 0.08)',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.16)',
          padding: '20px 22px',
          backdropFilter: 'blur(12px)'
        }}
      >
        <div style={{ display: 'grid', gap: '16px' }}>
          <div style={{ display: 'grid', gap: '8px' }}>
            <strong style={{ fontSize: '18px', color: '#111827' }}>{text.title}</strong>
            <p style={{ margin: 0, color: '#475467', lineHeight: 1.55 }}>{text.description}</p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
            <button
              type="button"
              onClick={handleAnalyticsAccepted}
              data-testid="cookie-accept"
              style={{
                border: '1px solid #111827',
                background: '#111827',
                color: '#fff',
                borderRadius: '999px',
                padding: '12px 16px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              {text.accept}
            </button>

            <button
              type="button"
              onClick={() => {
                AnalyticsConsentService.setState('denied');
                GoogleAnalyticsService.disableAnalytics();
                PostHogService.disable();
              }}
              style={{
                border: '1px solid #d0d5dd',
                background: '#fff',
                color: '#344054',
                borderRadius: '999px',
                padding: '12px 16px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              {text.reject}
            </button>

            <Link
              to="/legal"
              style={{
                color: '#344054',
                fontWeight: 600,
                textDecoration: 'underline'
              }}
            >
              {text.learnMore}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsConsentBanner;
